import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Generic sortable wrapper. Children get drag handle automatically.
 *
 * Usage:
 *   <SortableList items={rows} setItems={setRows} keyOf={(r) => r.id || r.slug}>
 *     {(item, dragHandleProps) => <YourCard item={item} dragHandle={dragHandleProps} />}
 *   </SortableList>
 */
export default function SortableList({ items, setItems, keyOf, children }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => keyOf(it) === active.id);
    const newIndex = items.findIndex((it) => keyOf(it) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex).map((it, i) => ({ ...it, order: i + 1 }));
    setItems(next);
  };

  const ids = items.map((it) => keyOf(it));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableItem key={keyOf(item)} id={keyOf(item)}>
            {(handleProps) => children(item, handleProps)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  const handleProps = { ...attributes, ...listeners };
  return (
    <div ref={setNodeRef} style={style}>
      {children(handleProps)}
    </div>
  );
}

/** Pre-styled drag handle component */
export function DragHandle({ ...props }) {
  return (
    <button
      type="button"
      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-navy p-1.5 touch-none"
      title="Sürükle"
      data-testid="drag-handle"
      {...props}
    >
      <GripVertical size={16} />
    </button>
  );
}
